import { useEffect, useRef } from "react";
import api from "@/lib/api";

/**
 * When enabled=true and location_sharing is on server-side, request browser
 * geolocation permission and ping the backend every intervalMs (default 60s).
 * We use watchPosition for smoother updates but only publish at most every
 * intervalMs to avoid hammering the API.
 */
export function useLocationSharing({ enabled, ghostMode = false, intervalMs = 60_000 }) {
  const watchIdRef = useRef(null);
  const lastPushAt = useRef(0);
  const lastCoords = useRef(null);

  useEffect(() => {
    if (!enabled) {
      if (watchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      return;
    }
    if (typeof navigator === "undefined" || !navigator.geolocation) return;

    const push = async (coords) => {
      if (ghostMode) return;
      try {
        await api.post("/location/ping", {
          lat: coords.latitude,
          lng: coords.longitude,
          accuracy: coords.accuracy || null,
        });
        lastPushAt.current = Date.now();
      } catch {}
    };

    const onSuccess = (pos) => {
      lastCoords.current = pos.coords;
      if (Date.now() - lastPushAt.current > intervalMs - 500) {
        push(pos.coords);
      }
    };

    const onError = () => {};

    watchIdRef.current = navigator.geolocation.watchPosition(onSuccess, onError, {
      enableHighAccuracy: false,
      maximumAge: 30_000,
      timeout: 20_000,
    });

    // Send initial ping right away
    navigator.geolocation.getCurrentPosition((pos) => push(pos.coords), () => {}, {
      enableHighAccuracy: false, maximumAge: 60_000, timeout: 15_000,
    });

    // Periodic re-publish so friends see fresh timestamps
    const interval = setInterval(() => {
      if (lastCoords.current) push(lastCoords.current);
    }, intervalMs);

    return () => {
      clearInterval(interval);
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [enabled, ghostMode, intervalMs]);
}
